/**
 * /legal · Concierge® · Terms, disclaimers, trademark & privacy notice.
 *
 * Placeholder copy · founder ruling 30 Jun 2026. Final wording to be
 * reviewed by counsel before any material claim is anchored to it.
 *
 * Entity placeholders to confirm:
 *   • [ENTITY NAME] — the registered company that owns Concierge®
 *   • [ENTITY ADDRESS] — registered address for service of process
 *   • [JURISDICTION] — governing law (recommended: India · Maharashtra)
 *   • [DPO_EMAIL] — Data Protection Officer / privacy contact
 *
 * Linked from:
 *   • TableFooter (sitewide · "Terms" pill next to trademark line)
 *   • Brief modal confirmation lines on every pillar (light reminder)
 */
import React from "react";
import { Link } from "react-router-dom";
import { TableFooter } from "@/components/table/TableFooter";

const T = {
  bg:    "#F4EFE6",
  card:  "#FCFAF4",
  ink:   "#211C15",
  ink2:  "#5C5447",
  ink3:  "#8A8071",
  line:  "#E5DCCB",
  brass: "#8B6914",
};

const SECTIONS = [
  {
    id: "directory",
    title: "1 · Venue listing & directory",
    body: (
      <>
        <p>
          Venues, hotels, restaurants, events, ateliers, and other establishments listed on
          Concierge® are surfaced from third-party data sources — including Google Places,
          Amadeus, Viator, and editorial sources curated by the Concierge®. Inclusion is not
          endorsement; we are not affiliated with the venues shown unless explicitly stated
          as a Concierge® partner.
        </p>
        <p>
          Information shown — opening hours, addresses, photos, ratings, reviews, prices,
          availability — is sourced from third parties and may change without notice. The
          Concierge® verifies all material details at the moment of a member brief.
        </p>
      </>
    ),
  },
  {
    id: "brief",
    title: "2 · A brief is a request, not a confirmed booking",
    body: (
      <>
        <p>
          When you tap <em>Send a brief</em> on any pillar, you are asking the Concierge®
          to source a venue, table, room, experience, or item on your behalf. This is
          a request &mdash; <strong>not a confirmed booking</strong>.
        </p>
        <p>
          The Concierge® replies with options, pricing, and confirmation via direct
          communication (typically email or WhatsApp) within target response windows.
          Response-time targets shown on the platform (e.g. &ldquo;within the hour&rdquo;) are
          editorial targets, not contractual guarantees.
        </p>
      </>
    ),
  },
  {
    id: "pricing",
    title: "3 · Pricing & availability",
    body: (
      <>
        <p>
          All prices, room rates, restaurant price tiers, and inventory shown on Concierge®
          are <strong>indicative only</strong> and subject to change. Verified pricing and
          availability are provided by the Concierge® at the time of confirmation, after we have
          checked with the venue or partner.
        </p>
        <p>
          For flight inventory (Amadeus), tour inventory (Viator), and Place inventory
          (Google), pricing reflects the partner&apos;s posted rates at the time the page
          rendered and may be stale by the time you act on it.
        </p>
      </>
    ),
  },
  {
    id: "content",
    title: "4 · Photos, reviews & editorial content",
    body: (
      <>
        <p>
          Venue photos shown on Concierge® are displayed via the Google Places Photos API
          and remain the property of their respective owners (the venue, the photographer,
          or Google). Concierge® does not own these images.
        </p>
        <p>
          Reviews shown on venue drawers are member-of-public reviews surfaced via the
          Google Places API. They reflect the views of their individual authors &mdash; not the
          editorial opinion of Concierge® or our desk.
        </p>
        <p>
          Where Concierge® provides its own editorial voice (the &ldquo;Concierge® voice&rdquo; line
          on Maison drawers, hand-curated picks, etc.), that content is identified visibly
          and may be generated with the assistance of large language models (Anthropic
          Claude) grounded on third-party facts.
        </p>
      </>
    ),
  },
  {
    id: "external",
    title: "5 · Third-party booking links",
    body: (
      <>
        <p>
          Links labelled <em>Reserve ↗</em>, <em>House site ↗</em>, <em>Directions ↗</em>,
          <em> Compare fares →</em>, and similar route to the venue&apos;s own booking page or
          to a third-party reservation provider (Google Reserve, SevenRooms, OpenTable,
          Amadeus, Viator, or others).
        </p>
        <p>
          Once you leave Concierge® via one of these links, the terms, privacy practices,
          cancellation policies, and consumer-protection rules of that third-party site
          apply. Concierge® is not party to the transaction that follows.
        </p>
      </>
    ),
  },
  {
    id: "service",
    title: "6 · Nature of the Concierge® service",
    body: (
      <>
        <p>
          Concierge® is a member-only concierge and introductions service operated under
          licence by{" "}
          <strong>Les Concierges, Club Concierge, and MindEscapes</strong>, licensees of
          the Concierge® mark owned by Dipali Sikand. Operations are headquartered in{" "}
          <strong>Bangalore, India</strong>.
        </p>
        <p>
          We facilitate introductions between members and providers (restaurants,
          hotels, airlines, ateliers, etc.). Where a member&apos;s brief results in a
          reservation, booking, purchase, or service delivery, <strong>the contract for
          those goods or services is between the member and the provider directly</strong>.
          Concierge® acts as an introducer and intermediary, not as the supplier.
        </p>
        <p>
          The Concierge® does, in some cases, hold confirmed inventory or pay deposits on a
          member&apos;s behalf &mdash; in those cases the role is custodial and disclosed at the
          moment of the brief.
        </p>
      </>
    ),
  },
  {
    id: "trademark",
    title: "7 · Trademarks",
    body: (
      <>
        <p>
          <strong>Concierge®</strong> is a registered trademark owned by{" "}
          <strong>Dipali Sikand</strong>, licensed to{" "}
          <strong>Les Concierges, Club Concierge, and MindEscapes</strong> for use on
          this platform and its partner-embedded widgets.
        </p>
        <p>
          Google, Google Maps, and Google Places are trademarks of Google LLC. Amadeus is
          a trademark of Amadeus IT Group, S.A. Viator is a trademark of Viator, Inc.
          Anthropic and Claude are trademarks of Anthropic, PBC. All other marks
          appearing on Concierge® belong to their respective owners.
        </p>
        <p>
          Use of these third-party marks on Concierge® is purely for identifying the
          underlying data source or service provider &mdash; it does not imply endorsement of
          Concierge® by these companies.
        </p>
      </>
    ),
  },
  {
    id: "privacy",
    title: "8 · Privacy & data handling",
    body: (
      <>
        <p>
          When you submit a brief, we collect: your name, email and/or phone, the city
          and category of the brief, and any free-text details you choose to share.
        </p>
        <p>
          This information is shared with:
        </p>
        <ul style={{ paddingLeft: 22, margin: "8px 0 0", lineHeight: 1.65 }}>
          <li>The Concierge® handling your brief.</li>
          <li>
            The partner venue or provider, only to the extent needed to fulfil your brief.
          </li>
          <li>
            Where the brief originates from an embedded partner widget (e.g. on
            mindescapes.club), the partner&apos;s own concierge desk for joint fulfilment.
          </li>
        </ul>
        <p>
          We do <strong>not</strong> sell your data to advertisers. We do not run
          third-party advertising on Concierge®.
        </p>
        <p>
          You can request access to, correction of, or deletion of your data at any time
          by writing to <strong>nisha@clubconcierge.in</strong>. The Concierge® will
          confirm within 30 days.
        </p>
        <p style={{ marginTop: 12 }}>
          <strong>Voice notes and transcription.</strong> Where you choose to record a
          voice note — for example, when whispering a dream through DreamWeaver — we
          transcribe it so your Concierge® can understand and act on your request.
          Transcription is carried out by a specialist third-party provider that
          processes the audio on servers located outside India (currently in the
          United States). We share only the audio necessary for transcription, for
          that purpose alone; the provider is bound by contract to process it on our
          instructions and not for its own purposes, and we do not transfer voice
          data to any territory restricted under the Digital Personal Data Protection
          Act, 2023. We retain the original audio so your Concierge® can hear the
          nuance of your request. You may use the text option instead if you prefer
          not to record a voice note, and you may ask us to delete a voice note and
          its transcription at any time.
        </p>
        <p style={{ marginTop: 12 }}>
          <strong>Continuity across pillars.</strong> With your consent (on by default,
          off at your word), your Concierge® may notice that a request in one
          pillar &mdash; for example, a stay in Bengaluru &mdash; is worth mentioning
          when you next visit another pillar (&ldquo;shall we hold a doctor close by?&rdquo;).
          This continuity draws only on your <em>requests</em> (city, category, dates)
          &mdash; never on the text of a whispered Dreamweaver dream, which lives in
          its own vessel and never contributes to a cross-pillar suggestion.
          You may turn this off in your profile at any time; the bands respect
          your quiet hours (default 20:00&ndash;08:00 IST) and step back gently
          each time you say &ldquo;noted.&rdquo;
        </p>
      </>
    ),
  },
  {
    id: "category",
    title: "9 · Category-specific notes",
    body: (
      <>
        <p>
          <strong>Wine, spirits & rare bottles.</strong> The Concierge® sources
          alcoholic beverages only on behalf of members who are of legal drinking age in
          the applicable jurisdiction. Delivery, import duties, and any age-verification
          checks are handled by the partner merchant, not by Concierge®.
        </p>
        <p>
          <strong>Jewellery, art & investment-grade goods.</strong> Concierge® does not
          provide investment advice. Editorial language around acquisitions, vintages,
          provenance, or investment merit is for member discovery only — not financial
          guidance. Independent valuation and counsel are recommended before any
          significant acquisition.
        </p>
        <p>
          <strong>Travel, flights & mobility.</strong> Flight inventory and pricing
          shown reflect Amadeus&apos;s GDS state at the time of the search. Final ticketed
          prices, fare rules, baggage allowances, and cancellation conditions are
          finalised at the time of booking by the issuing carrier.
        </p>
      </>
    ),
  },
  {
    id: "partner",
    title: "10 · Partner-embedded widgets",
    body: (
      <>
        <p>
          Some surfaces of Concierge® are made available as iframe widgets on partner
          sites (e.g. <em>mindescapes.club</em>) under licence.
        </p>
        <p>
          When you use Concierge® via a partner site, both the partner&apos;s privacy notice
          AND this Concierge® notice apply. Briefs submitted via partner-embedded
          widgets are co-handled by the partner&apos;s concierge desk and by Concierge®.
        </p>
      </>
    ),
  },
  {
    id: "governing",
    title: "11 · Governing law & changes",
    body: (
      <>
        <p>
          These terms are governed by the laws of <strong>India</strong>, with exclusive
          jurisdiction vested in the courts of <strong>Bangalore, Karnataka</strong>.
          Any dispute that cannot be resolved by the Concierge® in good faith is subject to the
          exclusive jurisdiction of these courts.
        </p>
        <p>
          We may amend these terms from time to time. Material changes will be
          surfaced via the footer link at least 30 days before they take effect for
          existing briefs.
        </p>
        <p>
          <strong>Other terms and conditions apply</strong> &mdash; including any
          specific terms agreed at the moment of a confirmed brief (e.g. cancellation
          policies, deposit terms, partner-venue-specific rules). These supplement, and
          do not replace, the general terms set out here.
        </p>
      </>
    ),
  },
];

export default function LegalPage() {
  return (
    <div
      data-testid="legal-page-root"
      style={{
        background: T.bg,
        minHeight: "100vh",
        color: T.ink,
        fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
        "--t-bg": T.bg, "--t-line": T.line, "--t-ink": T.ink, "--t-ink2": T.ink2,
        "--t-ink3": T.ink3, "--t-brass": T.brass, "--t-card": T.card,
      }}
    >
      {/* Top bar · matches the rest of the pillars */}
      <header
        style={{
          borderBottom: `1px solid ${T.line}`,
          padding: "14px 22px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 14,
        }}
      >
        <Link
          to="/"
          data-testid="legal-topbar-home"
          style={{ textDecoration: "none", color: T.ink, display: "inline-flex", alignItems: "baseline", gap: 10 }}
        >
          <span className="t-serif" style={{ fontSize: 22, fontWeight: 500, letterSpacing: "0.02em" }}>
            /legal
          </span>
          <span
            style={{
              fontFamily: "ui-monospace, monospace",
              fontSize: 11,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: T.ink3,
            }}
          >
            by Concierge®
          </span>
        </Link>
      </header>

      <main style={{ maxWidth: 760, margin: "0 auto", padding: "36px 24px 80px" }}>
        <p style={{
          fontSize: 10.5,
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          fontWeight: 600,
          color: T.brass,
          fontFamily: "ui-monospace, monospace",
          margin: "0 0 6px",
        }}>
          Terms · disclaimers · privacy
        </p>
        <h1
          className="t-serif"
          style={{
            fontSize: 38, fontWeight: 500, lineHeight: 1.12,
            margin: "0 0 14px", color: T.ink,
          }}
        >
          The fine print, plainly written.
        </h1>
        <p
          style={{
            fontSize: 15, color: T.ink2, lineHeight: 1.55,
            margin: "0 0 28px",
            maxWidth: 640,
          }}
        >
          Concierge® is a member-first service. This page sets out the listing,
          brief, pricing, content, and privacy practices that govern the
          platform &mdash; written to be read, not to be hidden in a vault.
          Last updated 30 June 2026.
        </p>

        {/* Anchor index */}
        <nav
          data-testid="legal-index"
          aria-label="Sections"
          style={{
            borderTop: `1px solid ${T.line}`,
            borderBottom: `1px solid ${T.line}`,
            padding: "16px 0",
            margin: "0 0 32px",
            display: "flex", flexWrap: "wrap", gap: "8px 18px",
          }}
        >
          {SECTIONS.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              style={{
                color: T.ink2,
                textDecoration: "none",
                fontSize: 12.5,
                fontFamily: "ui-monospace, monospace",
                letterSpacing: "0.04em",
                borderBottom: `1px dotted ${T.line}`,
              }}
            >
              {s.title.split(" · ")[1] || s.title}
            </a>
          ))}
        </nav>

        {SECTIONS.map((s) => (
          <section
            key={s.id}
            id={s.id}
            data-testid={`legal-section-${s.id}`}
            style={{ marginBottom: 36, scrollMarginTop: 24 }}
          >
            <h2
              className="t-serif"
              style={{
                fontSize: 22, fontWeight: 500, lineHeight: 1.22,
                margin: "0 0 12px", color: T.ink,
              }}
            >
              {s.title}
            </h2>
            <div
              style={{
                fontSize: 15, lineHeight: 1.65, color: T.ink2,
              }}
            >
              {s.body}
            </div>
          </section>
        ))}

        <section
          data-testid="legal-contact"
          style={{
            marginTop: 48,
            padding: "20px 22px",
            border: `1px solid ${T.line}`,
            borderRadius: 14,
            background: T.card,
          }}
        >
          <p style={{
            fontSize: 10.5, letterSpacing: "0.22em",
            textTransform: "uppercase", fontWeight: 600,
            color: T.brass, fontFamily: "ui-monospace, monospace",
            margin: 0,
          }}>
            Contact the Concierge®
          </p>
          <h3 className="t-serif" style={{ fontSize: 20, fontWeight: 500, margin: "6px 0 8px" }}>
            Questions, corrections, or data requests
          </h3>
          <p style={{ fontSize: 14, color: T.ink2, lineHeight: 1.55, margin: 0 }}>
            Write to <strong>nisha@clubconcierge.in</strong> or use the
            &ldquo;Ask the Concierge®&rdquo; pill on any pillar. The Concierge® responds within 30 days
            for data requests and within the hour for service questions.
          </p>
        </section>

        <p
          style={{
            fontSize: 11.5,
            color: T.ink3,
            fontStyle: "italic",
            textAlign: "center",
            margin: "40px 0 0",
            lineHeight: 1.6,
          }}
        >
          Other terms and conditions apply. Last updated 30 June 2026.
        </p>
      </main>

      <TableFooter slug="legal" tagline="The fine print, plainly written." />
    </div>
  );
}
